import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { productsApi } from '../lib/api';
import { ArrowRight, Camera, Lightbulb, Mic, Film, Box, Truck, Tag, PackageSearch, Send, PackageCheck, ClipboardCheck } from 'lucide-react';

const categoryIcons = {
  'IMAGE': Camera,
  'LUMIERE': Lightbulb,
  'MACHINERIE': Film,
  'AUDIO': Mic,
  'LOGISTIQUE': Truck,
  'VENTE': Tag,
};

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const SETTINGS_CACHE_KEY = 'pv_site_settings_cache_v1';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [menuHierarchy, setMenuHierarchy] = useState([]);
  // Hydrate from localStorage cache so the hero image starts downloading on
  // first paint — instead of waiting for the /admin/settings round-trip.
  const [siteSettings, setSiteSettings] = useState(() => {
    try {
      const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, menuRes, settingsRes] = await Promise.all([
          productsApi.getProducts({ limit: 10, sort: 'popular', exclude_sales: true, lang: i18n.language }),
          fetch(`${API_BASE}/menu/hierarchy`).then(r => r.json()),
          fetch(`${API_BASE}/admin/settings`).then(r => r.json())
        ]);
        setFeaturedProducts(productsRes.data.products || []);
        setMenuHierarchy(menuRes.menus || []);
        setSiteSettings(settingsRes);
        // Cache only the small `hero` slice to keep localStorage lean
        try {
          localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify({ hero: settingsRes?.hero }));
        } catch { /* quota / private mode — ignore */ }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [i18n.language]);

  const hero = siteSettings?.hero || {};
  const carouselImages = hero.carousel_images || [];
  const carouselEnabled = hero.carousel_enabled && carouselImages.length > 0;
  const carouselDuration = hero.carousel_duration || 5000;
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const handleHeroClick = (e) => {
    if (!carouselEnabled) return;
    const linkUrl = carouselImages[currentSlide]?.link_url;
    if (!linkUrl) return;
    if (e.target.closest('a, button')) return;
    // Absolute URL → external navigation; relative URL → in-app router navigation
    if (/^https?:\/\//i.test(linkUrl)) {
      // Detect "internal" absolute URLs (same origin or known staging/prod domain) → keep them in-app
      try {
        const url = new URL(linkUrl);
        const sameOrigin = url.origin === window.location.origin;
        const knownInternal = /\.puzzlevideo\.fr$/i.test(url.hostname);
        if (sameOrigin || knownInternal) {
          navigate(url.pathname + url.search + url.hash);
          return;
        }
      } catch { /* fall through to external nav */ }
      window.location.href = linkUrl;
      return;
    }
    navigate(linkUrl);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (!carouselEnabled || carouselImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % carouselImages.length);
    }, carouselDuration);
    return () => clearInterval(interval);
  }, [carouselEnabled, carouselImages.length, carouselDuration]);

  // Get current background image
  const heroBackground = carouselEnabled
    ? (carouselImages[currentSlide]?.url || hero.background_image)
    : (hero.background_image || '');
  const [heroImageFailed, setHeroImageFailed] = useState(false);
  useEffect(() => { setHeroImageFailed(false); }, [heroBackground]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Preload the hero image at the highest priority — sent in the <head>
          so the browser can start fetching it BEFORE the React tree mounts. */}
      {heroBackground && !heroImageFailed && (
        <Helmet>
          <link rel="preload" as="image" href={heroBackground} fetchPriority="high" />
        </Helmet>
      )}
      <SEO
        title="Accueil"
        description="Puzzle Video — Location de matériel audiovisuel professionnel à Paris. Caméras, optiques, lumières, machinerie, audio et grip. Devis gratuit en ligne."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Puzzle Video",
          "description": "Location de matériel audiovisuel professionnel",
          "address": { "@type": "PostalAddress", "addressLocality": "Paris", "addressCountry": "FR" },
          "telephone": "+33 1 45 85 81 76",
          "url": typeof window !== 'undefined' ? window.location.origin : ''
        }}
      />
      {/* Hero Section — keeps the image's NATURAL aspect ratio at all viewport widths.
          The <img> is set to w-full h-auto so it always touches both edges and
          its height scales proportionally. Text content is overlaid in absolute. */}
      {hero.enabled !== false && (
      <section
        className={`relative w-screen left-1/2 -translate-x-1/2 bg-black text-white overflow-hidden${carouselEnabled && carouselImages[currentSlide]?.link_url ? ' cursor-pointer' : ''}`}
        onClick={handleHeroClick}
        data-testid="hero-section"
        style={(!heroBackground || heroImageFailed) ? { aspectRatio: '16 / 6', backgroundImage: 'linear-gradient(135deg, #0a0a0a 0%, #1f2937 50%, #3c8ef2 100%)' } : undefined}
      >
        {/* Image layer — defines the section's intrinsic height via aspect ratio */}
        {carouselEnabled ? (
          carouselImages.map((img, idx) => (
            <img
              key={idx}
              src={img.url}
              alt=""
              loading={idx === 0 ? 'eager' : 'lazy'}
              fetchPriority={idx === 0 ? 'high' : 'auto'}
              decoding="async"
              onError={() => idx === currentSlide && setHeroImageFailed(true)}
              className={`w-full h-auto block transition-opacity duration-1000 ${idx === 0 ? 'relative' : 'absolute inset-0'}`}
              style={{
                opacity: idx === currentSlide ? 1 : 0,
                zIndex: idx === currentSlide ? 1 : 0,
              }}
            />
          ))
        ) : (
          heroBackground && !heroImageFailed && (
            <img
              src={heroBackground}
              alt=""
              loading="eager"
              fetchPriority="high"
              decoding="async"
              onError={() => setHeroImageFailed(true)}
              className="w-full h-auto block"
            />
          )
        )}
        {/* Dark gradient overlay (left -> right) to ensure text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent z-[2]" />

        {/* Text content — overlaid absolutely so it never alters the aspect ratio */}
        <div className="absolute inset-0 z-10 container mx-auto px-4 sm:px-6 md:px-12 lg:px-24 flex items-center">
          <div className="max-w-2xl">
            <h1 className="text-xl sm:text-4xl lg:text-6xl font-extrabold tracking-tighter mb-2 sm:mb-6 leading-tight" data-testid="hero-title">
              {t('home.hero_default_title')}
            </h1>
            <p className="hidden md:block text-lg text-gray-300 mb-8 leading-relaxed">
              {t('home.hero_default_subtitle')}
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-4">
              <Link to={hero.button_primary_link || '/catalogue'} data-testid="hero-cta-catalogue">
                <Button size="sm" className="rounded-none bg-primary hover:bg-primary-dark text-xs sm:text-base h-8 sm:h-11 px-3 sm:px-6">
                  {i18n.language === 'en' ? t('home.hero_cta_catalogue') : (hero.button_primary_text || t('home.hero_cta_catalogue'))}
                  <ArrowRight className="ml-1.5 sm:ml-2 w-3 sm:w-5 h-3 sm:h-5" />
                </Button>
              </Link>
              <Link to={hero.button_secondary_link || '/contact'} data-testid="hero-cta-contact">
                <Button size="sm" className="rounded-none bg-white text-black hover:bg-gray-100 text-xs sm:text-base h-8 sm:h-11 px-3 sm:px-6">
                  {i18n.language === 'en' ? t('home.hero_cta_quote') : (hero.button_secondary_text || t('home.hero_cta_quote'))}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Carousel indicators */}
        {carouselEnabled && carouselImages.length > 1 && (
          <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="carousel-indicators">
            {carouselImages.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.stopPropagation(); setCurrentSlide(idx); }}
                className={`w-2.5 h-2.5 rounded-full transition-all ${idx === currentSlide ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'}`}
                data-testid={`carousel-dot-${idx}`}
              />
            ))}
          </div>
        )}
      </section>
      )}

      {/* Categories */}
      <section className="py-24">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">{t('home.our_equipment')}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{t('home.categories')}</h2>
            </div>
            <Link to="/catalogue" className="hidden md:flex items-center gap-2 text-primary hover:underline">
              {t('home.view_all_short')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {menuHierarchy.map((menu) => {
              const Icon = categoryIcons[menu.name] || Box;
              return (
                <Link 
                  key={menu.name} 
                  to={`/catalogue?nav_menu=${encodeURIComponent(menu.name)}`}
                  data-testid={`category-${menu.name}`}
                >
                  <Card className="group border border-border rounded-none hover:shadow-md hover:-translate-y-1 transition-all duration-300 h-full">
                    <CardContent className="p-6 text-center">
                      <Icon className="w-10 h-10 mx-auto mb-4 text-primary group-hover:scale-110 transition-transform" />
                      <h3 className="font-semibold mb-1 text-sm">{menu.name}</h3>
                      <p className="text-xs text-muted-foreground">{menu.children?.length || 0} {t('home.subcategories')}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-3xl font-extrabold tracking-tight text-center mb-12">{t('home.how_it_works')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', Icon: PackageSearch, title: t('home.step_1_title'), desc: t('home.step_1_desc') },
              { step: '02', Icon: Send, title: t('home.step_2_title'), desc: t('home.step_2_desc') },
              { step: '03', Icon: ClipboardCheck, title: t('home.step_3_title'), desc: t('home.step_3_desc') },
              { step: '04', Icon: PackageCheck, title: t('home.step_4_title'), desc: t('home.step_4_desc') }
            ].map((item, index) => (
              <div key={index} className="group relative text-center" data-testid={`step-card-${item.step}`}>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-[calc(50%+2.5rem)] right-[calc(-50%+2.5rem)] h-px bg-gradient-to-r from-primary/40 to-transparent pointer-events-none" />
                )}
                <div className="relative inline-flex items-center justify-center w-16 h-16 mx-auto mb-5">
                  <span className="absolute inset-0 rounded-full bg-primary/10 group-hover:scale-110 transition-transform duration-300" />
                  <item.Icon strokeWidth={1.5} className="relative w-7 h-7 text-primary" />
                </div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary/70 mb-1">{item.step}</div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 bg-surface">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">{t('home.rental')}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{t('home.featured')}</h2>
            </div>
            <Link to="/catalogue" className="hidden md:flex items-center gap-2 text-primary hover:underline">
              {t('home.view_all_short')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-gray-200 mb-4" />
                  <div className="h-4 bg-gray-200 mb-2 w-1/4" />
                  <div className="h-5 bg-gray-200 mb-2" />
                  <div className="h-6 bg-gray-200 w-1/3" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} compact />
              ))}
            </div>
          )}

          <div className="mt-12 text-center md:hidden">
            <Link to="/catalogue">
              <Button variant="outline" className="rounded-none">
                {t('home.view_all')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-24 bg-primary text-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">
            {t('home.cta_title')}
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {t('home.cta_desc')}
          </p>
          <Link to="/contact">
            <Button size="lg" variant="outline" className="rounded-none border-white text-white hover:bg-white hover:text-primary">
              {t('home.cta_button')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
